/**
 * Enhanced translation process flow utilities
 * Organizes the step-by-step translation resolution process with i18n features
 */

import type { TranslationRequest } from '../types/translation.types.js';
import { createTranslationContext } from './context-utils.js';
import { parseNamespaceKey } from './namespace-utils.js';
import {
  createIndividualCacheKey,
  createNamespaceCacheKey,
} from './translation-value-utils.js';

/**
 * ✅ Enhanced: Parse and prepare translation request with cache keys
 * This is the entry point that organizes all the parsing logic
 */
export function prepareTranslationRequest(
  key: string,
  currentLocale: string,
  defaultValueOrParams?: string | Record<string, unknown>,
  paramsOrDefaultValue?: Record<string, unknown> | string,
  defaultNamespace?: string
): TranslationRequest {
  const startTime = performance.now();

  // Parse flexible parameters
  const { defaultValue, params } = parseTranslationParams(
    defaultValueOrParams,
    paramsOrDefaultValue
  );

  // Parse namespace from key
  const parsedKey = parseNamespaceKey(key);

  // Create translation context
  const context = createTranslationContext(
    parsedKey,
    currentLocale,
    defaultNamespace
  );

  // ✅ Generate cache keys using unified pattern
  const individualCacheKey = createIndividualCacheKey(
    context.locale,
    context.namespace,
    parsedKey.cleanKey
  );

  const namespaceCacheKey = context.namespace
    ? createNamespaceCacheKey(context.locale, context.namespace)
    : undefined;

  // Generate unique ID for the request (could be a hash or UUID)
  const id = `${individualCacheKey}-${startTime}`;

  return {
    id, // Unique request ID
    originalKey: key,
    originalParams: defaultValueOrParams,
    originalDefaultValue: paramsOrDefaultValue,
    cleanKey: parsedKey.cleanKey,
    hasExplicitNamespace: parsedKey.hasExplicitNamespace,
    parsedParams: params,
    parsedDefaultValue: defaultValue,
    locale: currentLocale,
    namespace: context.namespace, // ✅ Use effective namespace (explicit or default)
    individualCacheKey, // ✅ Ready-to-use cache key
    namespaceCacheKey, // ✅ Ready-to-use namespace key
    startTime,
  };
}

/**
 * Parse flexible translation parameters
 */
export function parseTranslationParams(
  secondParam?: string | Record<string, unknown>,
  thirdParam?: Record<string, unknown> | string
): { defaultValue?: string; params?: Record<string, unknown> } {
  let defaultValue: string | undefined;
  let params: Record<string, unknown> | undefined;

  // Runtime validation: Warn if both params are objects (likely misuse).
  // process is Node-only; browsers throw ReferenceError without the typeof
  // guard. Treat "no process" as "not production" so the dev warning still
  // fires in browser dev environments.
  const _isNotProd =
    typeof process === 'undefined' ||
    process.env?.NODE_ENV !== 'production';
  if (
    typeof secondParam === 'object' &&
    typeof thirdParam === 'object' &&
    _isNotProd
  ) {
    console.warn(
      '[LionRapid] Warning: Both secondParam and thirdParam are objects. ' +
        'Expected pattern: t(key, params, defaultValue) or t(key, defaultValue, params). ' +
        'Second object will be ignored as it cannot be a defaultValue.'
    );
  }

  if (typeof secondParam === 'string') {
    // t(key, defaultValue) or t(key, defaultValue, params)
    defaultValue = secondParam;
    if (typeof thirdParam === 'object') {
      params = thirdParam;
    }
  } else if (typeof secondParam === 'object') {
    // t(key, params) or t(key, params, defaultValue)
    params = secondParam;
    if (typeof thirdParam === 'string') {
      defaultValue = thirdParam;
    }
  }

  return { defaultValue, params };
}
